import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import DataTable from '../../data-table/DataTable';
import { useFilterContainerLoader } from '../../data-table/Loaders';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { buildActionParams, useInfrastructureListActions } from './hooks/UseInfrastructureListActions';
import { useInfrastructureSelectionState } from './hooks/UseInfrastructureSelectionState';
import { useFilterState } from '../../../stores/FilterStateStore';

/**
 * Base component for infrastructure and interface, list pages
 * Uses composition pattern to allow flexibility for different infrastructure types
 *
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.breadcrumbs - Page breadcrumbs
 * @param {string} props.pageId - Unique page identifier
 * @param {string} props.tableId - DataTable identifier
 * @param {Object} props.dataSource - Data source configuration
 * @param {Object} props.columns - DataTable columns configuration
 * @param {Object} props.sorters - Initial sort configuration
 * @param {string} props.emptyText - Text to show when table is empty
 * @param {string} props.idMapping - Field name for row ID mapping
 * @param {Object} [props.actionUrls={}] - Map of action names to API URLs (optional, defaults to empty object)
 * @param {Object} props.loaderOptions - Options for the data loader
 * @param {React.ReactNode} props.filterElement - Filter UI elements
 * @param {React.ReactNode} props.summaryElement - Optional summary component rendered above table (within DataStore context)
 * @param {Function} props.actionButtonsFn - Action button elements
 * @param {Function} props.customActionHandler - Optional custom handler for specific actions
 * @param {Function} props.columnTransform - Optional function to transform columns
 * @param {Object} [props.apiRef] - Ref object for external API access
 */
export default function InfrastructureListBase({
    title,
    breadcrumbs,
    pageId,
    tableId,
    dataSource,
    columns,
    sorters,
    emptyText,
    idMapping,
    actionUrls,
    loaderOptions,
    filterElement,
    summaryElement,
    actionButtonsFn,
    customActionHandler,
    columnTransform,
    apiRef,
}) {
    const [tableState, setTableState] = useState(undefined);

    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();
    const loader = useFilterContainerLoader(dataSource, loaderOptions);
    const [filterState] = useFilterState();

    const buildParams = useCallback(
        (customParams = {}) => {
            return buildActionParams(filterState, tableState, dataSource.params, customParams);
        },
        [filterState, tableState, dataSource.params]
    );

    useEffect(() => {
        if (apiRef) {
            apiRef.current = {
                buildParams,
            };
        }
    }, [apiRef, buildParams]);

    const { dataTableActionsDisabled, tableSelection, selectionData, selectionOptions } =
        useInfrastructureSelectionState(idMapping);

    const { dataTableActioning, handleConfirmedDataTableAction } = useInfrastructureListActions({
        dataSource,
        actionUrls,
        refresher,
        handleSuccessfulAction: undefined,
        customActionHandler,
        selectionData,
    });

    const isDataTableActionButton = dataTableActionsDisabled || dataTableActioning;

    const dataTableColumns = useMemo(
        () => (columnTransform ? columnTransform(columns) : columns),
        [columns, columnTransform]
    );

    const actionContent = useMemo(() => {
        return actionButtonsFn({
            isDataTableActionButton,
            dataTableActioning,
            selectionData,
            tableSelection,
            tableState,
            handleConfirmedDataTableAction,
            refresher,
            filterState,
        });
    }, [
        actionButtonsFn,
        isDataTableActionButton,
        dataTableActioning,
        selectionData,
        tableSelection,
        tableState,
        handleConfirmedDataTableAction,
        refresher,
        filterState,
    ]);

    const pageProps = isEmpty(title) ? { breadcrumbs } : { title };

    return (
        <AdminPage
            {...pageProps}
            pageId={pageId}
            isActioning={dataTableActioning}
            filterElement={filterElement}
            actions={<Actions>{actionContent}</Actions>}
        >
            <DataTable
                tableId={tableId}
                loader={loader}
                triggerRef={triggerRef}
                columns={dataTableColumns}
                visibleRows={0}
                showSelectionColumn
                sorters={sorters}
                onStateChanged={setTableState}
                emptyText={emptyText}
                selectionOptions={selectionOptions}
                summaryElement={summaryElement}
            />
        </AdminPage>
    );
}

InfrastructureListBase.propTypes = {
    title: PropTypes.string,
    breadcrumbs: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.arrayOf(
            PropTypes.shape({
                text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
                link: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
            })
        ),
    ]),
    pageId: PropTypes.string.isRequired,
    tableId: PropTypes.string.isRequired,
    dataSource: PropTypes.object.isRequired,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    sorters: PropTypes.arrayOf(
        PropTypes.shape({
            property: PropTypes.string.isRequired,
            direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
        })
    ),
    emptyText: PropTypes.string,
    idMapping: PropTypes.string.isRequired,
    actionUrls: PropTypes.objectOf(PropTypes.string),
    loaderOptions: PropTypes.object,
    filterElement: PropTypes.node,
    summaryElement: PropTypes.node,
    actionButtonsFn: PropTypes.func.isRequired,
    customActionHandler: PropTypes.func,
    columnTransform: PropTypes.func,
    apiRef: PropTypes.shape({
        current: PropTypes.object,
    }),
};

InfrastructureListBase.defaultProps = {
    title: '',
    breadcrumbs: [],
    sorters: [],
    emptyText: '',
    actionUrls: {},
    loaderOptions: {},
    filterElement: null,
    summaryElement: null,
    customActionHandler: undefined,
    columnTransform: undefined,
    apiRef: null,
};
